import { useEffect } from "react";
import { useLocation, Navigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { Home, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { StitchPage, StitchPanel } from "@/components/stitch";
import { SEO } from "@/components/SEO";
import { isNativeApp } from "@/lib/platform";
import { useAuth } from "@/contexts/AuthContext";

const NotFound = () => {
  const location = useLocation();
  const { session, loading } = useAuth();

  useEffect(() => {
    console.error("404 Error: User attempted to access non-existent route:", location.pathname);
  }, [location.pathname]);

  // Native rescue: if a Capacitor user ever lands on a 404 (e.g., after a
  // stray OAuth redirect, an old deep link, or a marketing URL that doesn't
  // exist in-app), don't show the dead-end "Signal Lost" screen — bounce them
  // straight to the auth screen or app root depending on session state.
  if (isNativeApp() && !loading) {
    return <Navigate to={session ? "/app" : "/auth"} replace />;
  }

  return (
    <>
      <SEO title="Page Not Found · FitAura AI" description="The page you're looking for doesn't exist." noindex />
    <StitchPage>
      <div className="flex min-h-screen items-center justify-center px-5">
        <StitchPanel className="w-full max-w-md p-8 text-center" glow>
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
            <AlertTriangle className="h-7 w-7" />
          </div>
          <h1 className="font-display mt-5 text-6xl font-bold uppercase tracking-tight text-foreground">
            <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              404
            </span>
          </h1>
          <p className="mt-2 font-display text-[10px] font-semibold uppercase tracking-[0.3em] text-primary/80">
            — SIGNAL LOST
          </p>
          <p className="mx-auto mt-4 max-w-xs text-sm text-muted-foreground">
            The runway you're looking for doesn't exist anymore.
          </p>
          <Button
            asChild
            className="font-display mt-6 bg-gradient-to-r from-primary to-primary/80 text-xs font-bold uppercase tracking-[0.2em] text-primary-foreground shadow-[0_0_30px_-6px_hsl(var(--primary))]"
          >
            <Link to="/">
              <Home className="mr-2 h-4 w-4" /> Back to Runway
            </Link>
          </Button>
        </StitchPanel>
      </div>
    </StitchPage>
    </>
  );
};

export default NotFound;
